import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, PenLine } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { interestEmoji, isInterestKey } from '../../lib/interests';
import { LIFE_EVENT_EMOJI, isLifeEventKey, visibleLifeEvents } from '../../lib/lifeEvents';
import type { LifeSelection } from '../../lib/lifeEvents';
import type { SurfacePreset } from './presets';
import type { IdeaTone, Selection } from './types';

const CUSTOM_VALUE = '__custom';
const NONE_VALUE = '__none';

interface ThemeChipRowProps {
  preset: SurfacePreset;
  rowKeys: string[];
  moreKeys: string[];
  /** The child's recent theme keys — shown as a small emoji hint on the right. */
  recent: string[];
  selection: Selection;
  onSelect: (next: Selection) => void;
  /** "Gerade los" life-event chip; absent = not shown. */
  lifeEvent?: { value: LifeSelection; onChange: (v: LifeSelection) => void };
  /** Dark pages (the first run, 2026-10-02): translucent chips instead of light pills. */
  tone?: IdeaTone;
}

const chipClass = (active: boolean, dark = false) =>
  `inline-flex items-center gap-1.5 min-h-10 pl-3 pr-3.5 py-2 rounded-full border-2 text-sm transition-colors ${
    active ? 'bg-primary border-primary text-white shadow-sm'
      : dark ? 'bg-white/[0.07] border-white/20 text-white/90 hover:bg-white/15'
        : 'bg-stone-100 border-transparent text-foreground hover:bg-stone-200'
  }`;
const triggerClass = (active: boolean, dashed = false, dark = false) =>
  `h-auto min-h-10 w-auto gap-1.5 pl-3 pr-3 py-2 rounded-full border-2 text-sm shadow-none focus:ring-0 [&>svg]:hidden ${
    active
      ? 'bg-primary border-primary text-white'
      : dark
        ? dashed
          ? 'bg-white/[0.07] border-dashed border-white/30 text-white/90 data-[placeholder]:text-white/90 hover:bg-white/15'
          : 'bg-white/[0.07] border-white/20 text-white/90 data-[placeholder]:text-white/90 hover:bg-white/15'
        : dashed
          ? 'bg-stone-100 border-dashed border-stone-300 text-foreground hover:bg-stone-200'
          : 'bg-stone-100 border-transparent text-foreground hover:bg-stone-200'
  }`;
const inputClass = 'flex-1 h-10 px-3 rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none text-sm bg-white';
const applyClass = 'h-10 px-4 rounded-xl bg-primary text-white text-sm font-medium disabled:opacity-50';

/**
 * The theme row: chips (recent first, then the most-searched), a "More"
 * dropdown with the other themes and a free-text topic, and — on the account
 * surface — the optional "Gerade los" life-event chip with its own free text.
 */
export default function ThemeChipRow({ preset, rowKeys, moreKeys, recent, selection, onSelect, lifeEvent, tone = 'light' }: ThemeChipRowProps) {
  const { t } = useTranslation();
  const dark = tone === 'dark';
  const hintClass = dark ? 'text-white/55' : 'text-muted-foreground';
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const life = lifeEvent?.value ?? null;
  const [lifeCustomOpen, setLifeCustomOpen] = useState(life?.kind === 'custom');
  const [lifeCustomText, setLifeCustomText] = useState(life?.kind === 'custom' ? life.text : '');
  const label = (key: string) => t(`interests.items.${key}`);
  const lifeLabel = (key: string) => t(`lifeEvents.items.${key}`);
  const lifeKeys = useMemo(() => visibleLifeEvents(), []);
  const p = preset.prefix;

  const selectKey = (key: string) => {
    if (selection?.kind === 'key' && selection.key === key) return;
    setCustomOpen(false);
    onSelect({ kind: 'key', key });
  };
  const applyCustom = () => {
    const text = customText.trim().slice(0, 40);
    if (!text) return;
    if (selection?.kind === 'custom' && selection.label === text) return;
    onSelect({ kind: 'custom', label: text });
  };
  const onMoreChange = (value: string) => {
    if (value === CUSTOM_VALUE) {
      // A typed topic handed over (reader → sign-up) starts the box off with its label.
      if (!customText && selection?.kind === 'custom') setCustomText(selection.label);
      setCustomOpen(true);
      return;
    }
    if (isInterestKey(value)) selectKey(value);
  };

  const onLifeChange = (value: string) => {
    if (!lifeEvent) return;
    if (value === CUSTOM_VALUE) { setLifeCustomOpen(true); return; }
    setLifeCustomOpen(false);
    if (value === NONE_VALUE) { if (life) lifeEvent.onChange(null); return; }
    if (isLifeEventKey(value) && !(life?.kind === 'key' && life.key === value)) lifeEvent.onChange({ kind: 'key', key: value });
  };
  const applyLifeCustom = () => {
    const text = lifeCustomText.trim().slice(0, 80);
    if (!text || !lifeEvent) return;
    if (life?.kind === 'custom' && life.text === text) return;
    lifeEvent.onChange({ kind: 'custom', text });
  };

  const moreValue = selection?.kind === 'key' && moreKeys.includes(selection.key)
    ? selection.key : selection?.kind === 'custom' ? CUSTOM_VALUE : '';
  const moreActive = moreValue !== '';
  const lifeValue = life ? (life.kind === 'key' ? life.key : CUSTOM_VALUE) : NONE_VALUE;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs ${hintClass}`}>{t('interests.createHint')}</span>
        {recent.length > 0 && (
          <span className={`text-[11px] ${hintClass}`} title={recent.map(label).join(', ')}>
            {t('create.picker.recentThemes')}: {recent.map(interestEmoji).join(' ')}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2" data-testid={preset.chipsRowTestId}>
        {rowKeys.map((key) => {
          const active = selection?.kind === 'key' && selection.key === key;
          return (
            <button key={key} type="button" onClick={() => selectKey(key)} aria-pressed={active} data-testid={preset.chipTestId(key)} className={chipClass(active, dark)}>
              <span aria-hidden="true" className="text-lg leading-none">{interestEmoji(key)}</span>
              {label(key)}
            </button>
          );
        })}
        <Select value={moreValue} onValueChange={onMoreChange}>
          <SelectTrigger aria-pressed={moreActive} data-testid={`${p}-more`} className={triggerClass(moreActive, false, dark)}>
            {selection?.kind === 'custom' ? (
              <span className="inline-flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" />{selection.label}</span>
            ) : moreActive ? (
              <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="text-lg leading-none">{interestEmoji(moreValue)}</span>{label(moreValue)}</span>
            ) : (
              <SelectValue placeholder={t('create.picker.more')} />
            )}
            <ChevronDown className="w-3.5 h-3.5 opacity-70" aria-hidden="true" />
          </SelectTrigger>
          <SelectContent>
            {moreKeys.map((key) => (
              <SelectItem key={key} value={key} data-testid={`${p}-more-option-${key}`}>
                <span aria-hidden="true" className="mr-1.5">{interestEmoji(key)}</span>{label(key)}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_VALUE} data-testid={`${p}-more-option-custom`}>
              <span aria-hidden="true" className="mr-1.5">✏️</span>{t('create.picker.customTheme')}
            </SelectItem>
          </SelectContent>
        </Select>
        {lifeEvent && (
          /* "Gerade los": optional, one chip, no extra row — the life-event half of the promise */
          <Select value={lifeValue} onValueChange={onLifeChange}>
            <SelectTrigger aria-pressed={life !== null} data-testid={`${p}-life`} className={triggerClass(life !== null, true, dark)}>
              {life?.kind === 'custom' ? (
                <span className="inline-flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" />{life.text}</span>
              ) : life ? (
                <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="text-lg leading-none">{LIFE_EVENT_EMOJI[life.key]}</span>{lifeLabel(life.key)}</span>
              ) : (
                <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="text-lg leading-none">🎒</span>{t('create.picker.lifeEvent')}</span>
              )}
              <ChevronDown className="w-3.5 h-3.5 opacity-70" aria-hidden="true" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE} data-testid={`${p}-life-option-none`}>{t('create.picker.lifeNone')}</SelectItem>
              {lifeKeys.map((key) => (
                <SelectItem key={key} value={key} data-testid={`${p}-life-option-${key}`}>
                  <span aria-hidden="true" className="mr-1.5">{LIFE_EVENT_EMOJI[key]}</span>{lifeLabel(key)}
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM_VALUE} data-testid={`${p}-life-option-custom`}>
                <span aria-hidden="true" className="mr-1.5">✏️</span>{t('create.picker.lifeCustom')}
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      {lifeEvent && lifeCustomOpen && (
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); applyLifeCustom(); }}>
          <input type="text" value={lifeCustomText} onChange={(e) => setLifeCustomText(e.target.value)} maxLength={80} autoFocus
            placeholder={t('create.picker.lifePlaceholder')} data-testid={`${p}-life-input`} className={inputClass} />
          <button type="submit" disabled={!lifeCustomText.trim()} data-testid={`${p}-life-apply`} className={applyClass}>
            {t('create.picker.customApply')}
          </button>
        </form>
      )}
      {customOpen && (
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); applyCustom(); }}>
          <input type="text" value={customText} onChange={(e) => setCustomText(e.target.value)} maxLength={40} autoFocus
            placeholder={t('create.picker.customPlaceholder')} data-testid={`${p}-custom-input`} className={inputClass} />
          <button type="submit" disabled={!customText.trim()} data-testid={`${p}-custom-apply`} className={applyClass}>
            {t('create.picker.customApply')}
          </button>
        </form>
      )}
    </div>
  );
}
